import { useState } from "react";
import { motion } from "framer-motion";
import { Share2, MessageCircle, Facebook, Linkedin, Link2, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

// Twitter/X monogram (lucide-react ships neither reliably; ship inline SVG)
const XIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2H21.5l-7.5 8.573L22.5 22h-6.828l-5.343-6.987L4.2 22H.94l8.02-9.166L.75 2H7.75l4.833 6.4L18.244 2Zm-1.196 18h1.88L7.05 4H5.06l11.988 16Z" />
  </svg>
);

export default function ShareButtons({ title = "", className = "", testid = "share", shareType = null, shareId = null }) {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  // For social crawlers we prefer the backend share URL which emits proper OG meta tags
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const shareUrl = shareType && (shareId !== null && shareId !== undefined)
    ? `${BACKEND}/api/share/${shareType}/${encodeURIComponent(shareId)}`
    : pageUrl;
  const enc = encodeURIComponent;
  const shareText = enc(`${title} — ${shareUrl}`);

  const items = [
    { key: "whatsapp", label: "WhatsApp", href: `https://wa.me/?text=${shareText}`, Icon: MessageCircle, color: "#25D366" },
    { key: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`, Icon: Facebook, color: "#1877F2" },
    { key: "x", label: "X", href: `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(shareUrl)}`, Icon: XIcon, color: "#ffffff" },
    { key: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`, Icon: Linkedin, color: "#0A66C2" },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked */ }
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-sm text-slate-400 me-1">
        <Share2 size={16} className="text-primary" />
        <span>{t.share.title}</span>
      </div>
      {items.map(({ key, label, href, Icon, color }) => (
        <motion.a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ y: -2, scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="h-10 w-10 rounded-full grid place-items-center border border-white/10 bg-white/5 hover:border-transparent hover:text-white transition-colors"
          style={{ ["--hover-bg"]: color }}
          onMouseEnter={(e) => { e.currentTarget.style.background = color; e.currentTarget.style.borderColor = color; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
          aria-label={label}
          data-testid={`${testid}-${key}`}
        >
          <Icon size={16} />
        </motion.a>
      ))}
      <motion.button
        whileHover={{ y: -2, scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={copy}
        className={`h-10 rounded-full px-4 grid place-items-center border text-sm font-semibold gap-2 inline-flex transition-colors ${copied ? "border-primary text-primary" : "border-white/10 bg-white/5 hover:border-primary/60"}`}
        aria-label={t.share.copy}
        data-testid={`${testid}-copy`}
      >
        {copied ? <><Check size={14} /> {t.share.copied}</> : <><Link2 size={14} /> {t.share.copy}</>}
      </motion.button>
    </div>
  );
}
